/* eslint-disable import/no-cycle */
/* eslint-disable no-underscore-dangle */
import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import { normalize } from 'normalizr';
import fetchBoothTabByIdRequest from 'network/fetchBoothTabById';
import {
  boothTabsAdapter,
  selectBoothTabById,
  selectBoothTabsByIds,
} from './selectors';
import {
  BoothTab,
  boothTabEntity,
  isContentElementPresentInLocale,
} from './types';
import { fetchCachedCMSData, getBoothExtraFields } from '../common/thunks';
import {
  setBoothLivestreams,
  setBoothVideoCallUrl,
  setBoothHasAttachment,
} from '../booths/reducers';

interface FetchBoothTabByIdProps {
  boothId: string;
  boothTabId: string;
  locale: string;
}

export const fetchBoothTabById = createAsyncThunk(
  'boothTabs/fetchBoothTabById',
  async ({ boothId, boothTabId, locale }: FetchBoothTabByIdProps, { getState, dispatch }) => {
    try {
      const state = getState();
      const tab = selectBoothTabById(state, boothTabId);

      const result = await fetchBoothTabByIdRequest({
        tabId: boothTabId,
        locales: [locale],
      });
      const { entities } = normalize<BoothTab>([tab, { ...result, boothId }], [boothTabEntity]);

      const { boothTabs } = entities;
      const {
        livestreams,
        videoCallUrl,
        hasAttachment,
      } = getBoothExtraFields(boothTabs[boothTabId]);
      batch(() => {
        if (livestreams) dispatch(setBoothLivestreams({ boothId, livestreams }));
        if (videoCallUrl) dispatch(setBoothVideoCallUrl({ boothId, videoCallUrl }));
        if (hasAttachment) dispatch(setBoothHasAttachment({ boothId, hasAttachment }));
      });

      return boothTabs;
    } catch (error) {
      console.error(error);
    }
    return {};
  },
  {
    condition({ boothTabId, locale }: FetchBoothTabByIdProps, { getState }) {
      const state = getState();
      const tab = selectBoothTabById(state, boothTabId);
      return !tab?.body?.some((ele) => isContentElementPresentInLocale(ele, locale));
    },
  },
);

export const boothTabsSlice = createSlice({
  name: 'boothTabs',
  initialState: boothTabsAdapter.getInitialState({
    pageInfo: {
      hasNextPage: true,
      endCursor: null,
      fetching: false,
    },
  }),
  reducers: {
    setBoothTabs(state, action) {
      const { payload } = action;
      const tabIds = Object.keys(payload);
      const existingTabs = tabIds.map((id) => state.entities[id]).filter((t) => !!t);
      const { entities: { boothTabs } } = normalize<BoothTab>(
        [...existingTabs, ...Object.values(payload)],
        [boothTabEntity],
      );
      boothTabsAdapter.upsertMany(state, boothTabs);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCachedCMSData.fulfilled, (state, action) => {
      const { boothTabs } = action.payload;
      if (boothTabs && Object.keys(boothTabs).length) {
        boothTabsAdapter.upsertMany(state, (boothTabs as any));
      }
    });

    builder.addCase(fetchBoothTabById.pending, (state, action) => {
      const { boothTabId } = action.meta.arg;
      boothTabsAdapter.upsertOne(state, { id: boothTabId, $fetching: true } as any);
    });
    builder.addCase(fetchBoothTabById.rejected, (state, action) => {
      const { boothTabId } = action.meta.arg;
      boothTabsAdapter.upsertOne(state, { id: boothTabId, $fetching: false } as any);
    });
    builder.addCase(fetchBoothTabById.fulfilled, (state, action) => {
      const { boothTabId } = action.meta.arg;
      if (action.payload && Object.keys(action.payload).length) {
        boothTabsAdapter.upsertOne(state, { ...action.payload[boothTabId], $fetching: false });
      }
    });
  },
});

export const { setBoothTabs } = boothTabsSlice.actions;

export default boothTabsSlice.reducer;
